import React, {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import assessmentViewsMap, {
  AssessmentViewProps,
} from "@features/assessments-feature/utils/assessmentViewsMap";
import {
  assessmentTabIds,
  assessmentTabViewMode,
} from "@features/assessments-feature/constants";
import { assessmentTabViewModeMap } from "@features/assessments-feature/utils/assessmentViewMode";
import { ResponseRecommendation } from "@lib/ShiOneClient";

interface AssessmentViewContextState {
  assessmentViewProps?: AssessmentViewProps;
  currentTabViewMode?: assessmentTabViewMode;
  handleViewMode: (tab: assessmentTabIds, mode: assessmentTabViewMode) => void;
  tab: assessmentTabIds;
  assessmentId: string | null;
  program: string;
  selectedSourceIds: Map<string, string>;
  handleCheckRecommendation: (
    event: React.ChangeEvent<HTMLInputElement>,
    recData?: ResponseRecommendation,
  ) => void;
  isOpenSendToDialog: boolean;
  setIsOpenSendToDialog: (open: boolean) => void;
}

const AssessmentContext = createContext<AssessmentViewContextState | undefined>(
  undefined,
);

export function useAssessmentViewContext() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error(
      "AssessmentViewContext must be used within a AssessmentViewContextProvider",
    );
  }
  return context;
}

interface Props {
  program: string;
  children: React.ReactNode;
}

const AssessmentViewContextProvider = ({ children, program }: Props) => {
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("aid");
  const { type } = useParams();

  const isTabValid =
    !!type &&
    Object.values(assessmentTabIds).includes(type as assessmentTabIds);

  const assessmentViewProps = assessmentViewsMap.get(program ?? "");

  const [isOpenSendToDialog, setIsOpenSendToDialog] = useState(false);
  const [tab, setTab] = useState<assessmentTabIds>(
    isTabValid ? (type as assessmentTabIds) : assessmentTabIds.overview,
  );
  const [currentTabViewMode, setCurrentTabViewMode] = useState<
    assessmentTabViewMode | undefined
  >(assessmentTabViewModeMap.get(tab));
  const [selectedSourceIds, setSelectedSourceIds] = useState<
    Map<string, string>
  >(new Map());

  useEffect(() => {
    handleChangeTab(
      isTabValid ? (type as assessmentTabIds) : assessmentTabIds.overview,
    );
  }, [assessmentId, type]);

  const handleChangeTab = (tab: assessmentTabIds) => {
    setTab(tab);
    setCurrentTabViewMode(assessmentTabViewModeMap.get(tab));
  };
  const handleViewMode = (
    tab: assessmentTabIds,
    mode: assessmentTabViewMode,
  ) => {
    startTransition(() => {
      assessmentTabViewModeMap.set(tab, mode);
      setCurrentTabViewMode(assessmentTabViewModeMap.get(tab));
    });
  };

  const handleCheckRecommendation = (
    event: React.ChangeEvent<HTMLInputElement>,
    recData?: ResponseRecommendation,
  ) => {
    const capability = recData?.capability!;
    const constructedSourceId = `${recData?.assessmentId}:${recData?.recommendationId}`;

    setSelectedSourceIds((prevState) => {
      const newMap = new Map(prevState);
      if (event.target.checked) {
        newMap.set(constructedSourceId, capability);
      } else {
        newMap.delete(constructedSourceId);
      }
      return newMap;
    });
  };

  const value = useMemo(
    () => ({
      assessmentViewProps,
      currentTabViewMode,
      handleViewMode,
      tab,
      assessmentId,
      program,
      selectedSourceIds,
      handleCheckRecommendation,
      isOpenSendToDialog,
      setIsOpenSendToDialog,
    }),
    [
      assessmentViewProps,
      handleViewMode,
      currentTabViewMode,
      tab,
      assessmentId,
      program,
      selectedSourceIds,
      handleCheckRecommendation,
      isOpenSendToDialog,
      setIsOpenSendToDialog,
    ],
  );
  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export default AssessmentViewContextProvider;
